/**
 * lexDomain — lex.ivoliq Host-/Modus-Erkennung (Juni 2026).
 *
 * lex.ivoliq läuft auf einer eigenen Domain:
 *   PROD: https://lex.ivoliq.io
 *   DEV:  https://dev.lex.ivoliq.io
 *
 * In Preview/localhost gibt es keine Subdomain — dort kann der lex-Modus
 * per ?lex=1 aktiviert (persistiert in localStorage) und per ?lex=0
 * deaktiviert werden. Zusätzlich existiert die Route /lex-login.
 */

const LEX_HOSTS = ["lex.ivoliq.io", "www.lex.ivoliq.io", "dev.lex.ivoliq.io"];

// Feature-Flag (Build-Zeit): lex.ivoliq wird in Prod zurückgehalten.
// REACT_APP_LEX_ENABLED=true → DEV/Preview; fehlt/false → Prod (lex unsichtbar).
export const isLexEnabled = () => process.env.REACT_APP_LEX_ENABLED === "true";

// Landing-only-Modus (Lead-Start): Die lex-Landing + Warteliste sind sichtbar,
// aber Login/Registrierung/App bleiben aus (LEX_ENABLED=false in Prod).
export const isLexLandingOnly = () => process.env.REACT_APP_LEX_LANDING_ONLY === "true";

export const isLexHost = () => LEX_HOSTS.includes(window.location.hostname);

// lex-Host → zugehörige Hauptdomain (Aug 2026, Produkt-Trennung)
const LEX_TO_MAIN = {
  "lex.ivoliq.io": "ivoliq.io",
  "www.lex.ivoliq.io": "ivoliq.io",
  "dev.lex.ivoliq.io": "dev.ivoliq.io",
};

// Hauptdomain → lex-Host (Sep 2026, strikte Produkt-Trennung: kein lex auf ivoliq.io)
const MAIN_TO_LEX = {
  "ivoliq.io": "lex.ivoliq.io",
  "www.ivoliq.io": "lex.ivoliq.io",
  "dev.ivoliq.io": "dev.lex.ivoliq.io",
};

/** Sind wir auf einer ivoliq-Hauptdomain (PROD/DEV)? Preview/localhost: false. */
export const isMainHost = () => Boolean(MAIN_TO_LEX[window.location.hostname]);

/** Absolute lex-Host-URL, wenn wir auf einer Hauptdomain sind — sonst null (Preview: null). */
export const lexHostUrl = (path) => {
  const lexHost = MAIN_TO_LEX[window.location.hostname];
  return lexHost ? `https://${lexHost}${path}` : null;
};

/** lex-exklusive Pfade. ACHTUNG: /lexoffice-alternative ist eine ivoliq-SEO-Seite — kein lex-Pfad! */
export const isLexPath = (pathname) =>
  pathname === "/lex" || pathname.startsWith("/lex/") ||
  ["/lex-login", "/lex-impressum", "/lex-datenschutz", "/lex-agb", "/kanzlei"]
    .some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Hauptdomain (nur PROD/DEV): lex-Pfade hart zum lex-Host umleiten.
 * Preview/localhost: neutral. true = Redirect ausgelöst → nicht weiterrendern.
 */
export function enforceLexDomain(pathname, search = "") {
  if (!isMainHost() || !isLexPath(pathname)) return false;
  window.location.replace(lexHostUrl(pathname + search));
  return true;
}

/** Absolute Hauptdomain-URL, wenn wir auf einem lex-Host sind — sonst null. */
export const lexMainUrl = (path) => {
  const main = LEX_TO_MAIN[window.location.hostname];
  return main ? `https://${main}${path}` : null;
};

/** Preview/localhost: gespeicherten lex-Modus beenden (FL-User gehört nicht in lex). */
export const clearLexMode = () => {
  try { localStorage.removeItem("lex_mode"); } catch (_) {}
};

export function isLexMode(search = "") {
  // Strikte Produkt-Trennung (Sep 2026): Auf der Hauptdomain gibt es KEINEN lex-Modus —
  // ?lex=1 und alte localStorage-Flags werden ignoriert und selbstheilend entfernt.
  if (isMainHost()) {
    try { localStorage.removeItem("lex_mode"); } catch (_) {}
    return false;
  }
  if (!isLexEnabled() && !isLexLandingOnly()) return false;
  const params = new URLSearchParams(search || window.location.search);
  if (params.get("lex") === "1") {
    try { localStorage.setItem("lex_mode", "1"); } catch (_) {}
    return true;
  }
  if (params.get("lex") === "0") {
    try { localStorage.removeItem("lex_mode"); } catch (_) {}
    return false;
  }
  if (isLexHost()) return true;
  try { return localStorage.getItem("lex_mode") === "1"; } catch (_) { return false; }
}

/** Produkt-Zuordnung eines Users/Mandanten (unabhängig vom Host). Zentrale Stelle — nie inline prüfen. */
export const isLexUser = (user) => user?.account_variant === "lex";

/** lex-Kontext: lex-Host/-Modus, eingeloggter lex-User ODER lex-Seitenpfad (/lex, /lex-login, /lex-impressum …). */
export const isLexContext = (user) =>
  isLexMode() || isLexUser(user) || (typeof window !== "undefined" &&
    (window.location.pathname.startsWith("/lex") || window.location.pathname.startsWith("/kanzlei")));
